// Page: CreateMailTemplate
// Purpose: Admin flow to create a mail template with front/back images, tags,
// and AI-assisted metadata. Validates input and posts via multipart FormData.
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { ButtonSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { auth } from '../../../firebase/firebase';
import { SERVER_URL } from '../../../api/core';
import { useAuth } from '../../../context/auth';
import { Sparkles, ArrowLeft, Upload } from 'lucide-react';
import { isUserAdmin } from '../../../utils/admin/adminUtils';
import { MAIL_TEMPLATE_TAG_LIST } from '../../../data/tags/tagEnums';
// Remove mail templates API import since we're using the templates collection now
import { MailAIModal } from '../../../components/admin/createTemplates/MailAIModal';

// Error boundary wrapper component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Form submission error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-red-50 rounded-md dark:bg-red-900/20">
          <h3 className="text-lg font-medium text-red-800 dark:text-red-300">Something went wrong</h3>
          <p className="mt-2 text-sm text-red-700 dark:text-red-400">
            {this.state.error?.message || "An unexpected error occurred. Please try again."}
          </p>
          <button 
            className="mt-3 px-4 py-2 bg-red-100 text-red-800 rounded-md hover:bg-red-200 dark:bg-red-800 dark:text-red-200 dark:hover:bg-red-700"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const CreateMailTemplate = ({ setCurrentView = null }) => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    searchTerms: '',
    canvaLink: '',
    tags: []
  });
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [frontImagePreview, setFrontImagePreview] = useState(null);
  const [backImagePreview, setBackImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      setIsLoading(true);
      
      try {
        if (user) {
          const userIsAdmin = isUserAdmin(user);
          console.log("User is admin:", userIsAdmin);
          
          if (userIsAdmin) {
            console.log('User authorized as admin');
            setIsAuthorized(true);
          } else {
            console.log('User is not authorized as admin');
            navigate('/app');
          }
        } else if (user === null) {
          console.log('No authenticated user found');
          navigate('/login');
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        navigate('/app');
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAdmin();
  }, [navigate, user]);

  // Reset the page when unmounting
  useEffect(() => {
    return () => {
      setIsSubmitting(false);
      setSubmitError('');
      setSuccessMessage('');
      setIsSuccessful(false);
    };
  }, []);

  // Auto-dismiss error and success messages
  useEffect(() => {
    if (submitError || successMessage) {
      const timer = setTimeout(() => {
        if (submitError) setSubmitError('');
        if (successMessage) setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [submitError, successMessage]);

  // Navigate when successful
  useEffect(() => {
    let timer;
    if (isSuccessful) {
      timer = setTimeout(() => {
        // Navigate to the main manage templates page
        navigate('/app/private/manageTemplates');
      }, 1500);
    }
    return () => clearTimeout(timer);
  }, [isSuccessful, navigate]);

  // Handle image file selection
  const handleImageChange = (type, file) => {
    if (type === 'front') {
      setFrontImage(file);
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => setFrontImagePreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setFrontImagePreview(null);
      }
    } else if (type === 'back') {
      setBackImage(file);
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => setBackImagePreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setBackImagePreview(null);
      }
    }
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
  };

  // Handle tag selection
  const handleTagToggle = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
    setTouched(prev => ({ ...prev, tags: true }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.searchTerms.trim()) {
      newErrors.searchTerms = 'Search terms are required';
    }
    
    if (!formData.canvaLink.trim()) {
      newErrors.canvaLink = 'Canva link is required';
    } else if (!formData.canvaLink.includes('canva.com')) {
      newErrors.canvaLink = 'Please enter a valid Canva link';
    }
    
    if (formData.tags.length === 0) {
      newErrors.tags = 'At least one tag is required';
    }
    
    if (!frontImage) {
      newErrors.frontImage = 'Front image is required';
    }
    
    if (!backImage) {
      newErrors.backImage = 'Back image is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (isSubmitting) {
      console.log('Already submitting, ignoring request');
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitError('');
    setIsSubmitting(true);
    
    try {
      console.log('Starting mail template submission...');
      
      // Create FormData
      const submitFormData = new FormData();
      submitFormData.append('searchTerms', formData.searchTerms);
      submitFormData.append('canvaLink', formData.canvaLink);
      submitFormData.append('tags', JSON.stringify(formData.tags));
      submitFormData.append('frontImage', frontImage);
      submitFormData.append('backImage', backImage);
      
      // Make API request to templateManagement create-mail endpoint
      const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      
      if (!idToken) {
        throw new Error('Not authenticated. Please log in again.');
      }

      const response = await fetch(`${SERVER_URL}/templateManagement/create-mail`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        body: submitFormData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create mail template');
      }

      const result = await response.json();
      console.log('Mail template created successfully:', result);
      
      setSuccessMessage('Mail template created successfully!');
      setIsSuccessful(true);
      addToast('Mail template created successfully!', 'success');
      
    } catch (error) {
      console.error('Error creating mail template:', error);
      setSubmitError(error.message || 'Failed to create mail template');
      addToast('Failed to create mail template', 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, frontImage, backImage, isSubmitting, addToast]);

  // Handle AI generation success
  const handleAIGenerateSuccess = (aiData) => {
    console.log('AI generated mail data:', aiData);
    
    // Update form data with AI generated data
    if (aiData.searchTerms) {
      setFormData(prev => ({ ...prev, searchTerms: aiData.searchTerms }));
      setTouched(prev => ({ ...prev, searchTerms: true }));
    }
    
    if (aiData.tags && Array.isArray(aiData.tags) && aiData.tags.length > 0) {
      setFormData(prev => ({ ...prev, tags: aiData.tags }));
      setTouched(prev => ({ ...prev, tags: true }));
    }
    
    addToast('Mail template data generated successfully!', 'success');
  };

  // Open AI modal
  const handleAIGenerate = () => {
    setIsAIModalOpen(true);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthorized) {
    return null;
  }

  return (
    <ErrorBoundary>
      <div className="p-6 w-full max-w-[1600px] mx-auto">
        {/* Back to Admin Dashboard button */}
        <div className="mb-4">
          <button
            onClick={() => setCurrentView ? setCurrentView('adminMailTemplates') : navigate('/app/private/admin/templates')}
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            {setCurrentView ? 'Back to Mail Templates' : 'Back to Templates Admin'}
          </button>
        </div>

        <div className="mb-6 flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Create Mail Template</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Fill out the form below to create a new mail template with front and back images.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/app/private/manageTemplates')}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
            >
              Manage Templates
            </button>
            <button
              onClick={handleAIGenerate}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Sparkles className="h-4 w-4" />
              Use AI
            </button>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm p-6">
          {/* Error and Success Messages */}
          {submitError && (
            <div className="mb-4 rounded-md bg-red-50 p-4 dark:bg-red-900/20">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400 dark:text-red-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error Creating Mail Template</h3>
                  <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                    {submitError}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {successMessage && (
            <div className="mb-4 rounded-md bg-green-50 p-4 dark:bg-green-900/20">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400 dark:text-green-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.236 4.53L8.23 10.66a.75.75 0 00-1.06 1.061l2.5 2.5a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800 dark:text-green-200">Success!</h3>
                  <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                    {successMessage}
                  </div>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
              {/* Search Terms */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Search Terms *
                </label>
                <textarea
                  value={formData.searchTerms}
                  onChange={(e) => handleInputChange('searchTerms', e.target.value)}
                  rows={3}
                  className={`block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500 ${
                    errors.searchTerms ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="Enter search terms separated by commas"
                />
                {errors.searchTerms && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.searchTerms}</p>
                )}
              </div>

              {/* Canva Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Canva Link *
                </label>
                <input
                  type="url"
                  value={formData.canvaLink}
                  onChange={(e) => handleInputChange('canvaLink', e.target.value)}
                  className={`block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500 ${
                    errors.canvaLink ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="https://canva.com/design/..."
                />
                {errors.canvaLink && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.canvaLink}</p>
                )}
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tags *
                </label>
                <div className="flex flex-wrap gap-2">
                  {MAIL_TEMPLATE_TAG_LIST.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => handleTagToggle(tag)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        formData.tags.includes(tag)
                          ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
                {errors.tags && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.tags}</p>
                )}
              </div>

              {/* Images */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Front Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Front Image *
                  </label>
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    errors.frontImage 
                      ? 'border-red-500 dark:border-red-400' 
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                  }`}>
                    {frontImagePreview ? (
                      <div className="space-y-2">
                        <img
                          src={frontImagePreview}
                          alt="Front preview"
                          className="w-full h-48 object-cover rounded-md"
                        />
                        <button
                          type="button"
                          onClick={() => handleImageChange('front', null)}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                        <p className="text-gray-600 dark:text-gray-400 mb-2">Upload front image</p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageChange('front', e.target.files[0])}
                          className="hidden"
                          id="front-image"
                        />
                        <label
                          htmlFor="front-image"
                          className="inline-block px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 cursor-pointer transition-colors"
                        >
                          Choose File
                        </label>
                      </div>
                    )}
                  </div>
                  {errors.frontImage && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.frontImage}</p>
                  )}
                </div>

                {/* Back Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Back Image *
                  </label>
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    errors.backImage 
                      ? 'border-red-500 dark:border-red-400' 
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                  }`}>
                    {backImagePreview ? (
                      <div className="space-y-2">
                        <img
                          src={backImagePreview}
                          alt="Back preview"
                          className="w-full h-48 object-cover rounded-md"
                        />
                        <button
                          type="button"
                          onClick={() => handleImageChange('back', null)}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                        <p className="text-gray-600 dark:text-gray-400 mb-2">Upload back image</p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageChange('back', e.target.files[0])}
                          className="hidden"
                          id="back-image"
                        />
                        <label
                          htmlFor="back-image"
                          className="inline-block px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 cursor-pointer transition-colors"
                        >
                          Choose File
                        </label>
                      </div>
                    )}
                  </div>
                  {errors.backImage && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.backImage}</p>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed dark:focus:ring-offset-gray-900"
                >
                  {isSubmitting ? (
                    <>
                      <ButtonSpinner />
                      Creating...
                    </>
                  ) : (
                    'Create Mail Template'
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Mail AI Modal */}
          <MailAIModal
            isOpen={isAIModalOpen}
            onClose={() => setIsAIModalOpen(false)}
            onGenerateSuccess={handleAIGenerateSuccess}
          />
        </div>
    </ErrorBoundary>
  );
};

export default CreateMailTemplate; 